'use client';

import {
  useRouter,
  useSearchParams,
  useParams,
  usePathname,
} from 'next/navigation';
import { camelCase } from 'lodash';
import { useEffect, useState } from 'react';

/* ================================== TYPES ================================= */

export type TUrlParams = { [key: string]: string | undefined };

export type TSearchQueryParams = { [key: string]: string };

export type TNavigateToParams = {
  url?: string;
  queryParams?: TSearchQueryParams;
  forceNavigation?: boolean; // If true, the page will be reloaded if the URL / params are the same
};

type TNavigationHook = {
  urlParams: { [key: string]: string | undefined };
  queryParams: { [key: string]: string | undefined };
  navigateTo: (params: TNavigateToParams) => void;
  currentUrl: string;
};

/* ========================================================================== */
/*                                    HOOK                                    */
/* ========================================================================== */
export const useNavigation = (): TNavigationHook => {
  const router = useRouter();
  const pathname = usePathname();
  const params = useParams();
  const searchParams = useSearchParams();

  const [currentUrl, setCurrentUrl] = useState(() => {
    // Ensure window is available before using it
    if (typeof window !== 'undefined') {
      return `${window.location.origin}${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    }
    return '';
  });

  const urlParams: { [key: string]: string | undefined } = {};
  const queryParams: TSearchQueryParams = {};

  // Add URL parameters
  if (typeof params === 'object') {
    const originalParams = params as TUrlParams;

    Object.keys(originalParams).forEach((key) => {
      const camelCaseKey = camelCase(key);
      urlParams[camelCaseKey] = String(originalParams[key]);
    });
  }

  // Add search query parameters
  if (typeof searchParams === 'object') {
    searchParams.forEach((value, key) => {
      const camelCaseKey = camelCase(key);
      queryParams[camelCaseKey] = value;
    });
  }

  const navigateTo = ({
    url,
    queryParams: newQueryParams,
    forceNavigation = false, // Default to false
  }: TNavigateToParams) => {
    // Get the current URL
    const currentUrlString = `${window.location.origin}${pathname}${
      searchParams.toString() ? `?${searchParams.toString()}` : ''
    }`;
    const currentUrlObject = new URL(currentUrlString);

    // Determine the target URL
    const targetUrlString = url ? url : currentUrlString;
    const targetUrlObject = new URL(targetUrlString, window.location.origin);

    // Clear existing query params from target URL
    targetUrlObject.search = '';

    // Add new query params to target URL
    if (newQueryParams) {
      for (const [key, value] of Object.entries(newQueryParams)) {
        targetUrlObject.searchParams.set(key, value);
      }
    }

    // Compare the current URL and target URL (including query params)
    const currentFullUrl = currentUrlObject.toString();
    const targetFullUrl = targetUrlObject.toString();

    // Navigate if URLs differ or if forceNavigation is true
    if (forceNavigation || currentFullUrl !== targetFullUrl) {
      // Use push or replace based on whether a new URL is provided
      if (url) {
        router.push(targetUrlObject.href, { scroll: false });
      } else {
        router.replace(targetUrlObject.href, { scroll: false });
      }
    }
  };

  useEffect(() => {
    setCurrentUrl(
      `${window.location.origin}${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
    );
  }, [pathname, searchParams]);

  return { urlParams, queryParams, navigateTo, currentUrl };
};
